import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import { WorkflowEnedis } from "@types"

export type GetWorkflowEnedisByCompanyIdResponse = {
    data: WorkflowEnedis[]
    total: number
}

/**
 * Réponse 200 de GET /api/metier/v5/source/search/pdl (source.get.search.idtype.route.js).
 * Shape exacte renvoyée par l'API : { source, consentement, hasAccess }.
 */
export type VerifyPdlResult = {
    /** Source trouvée (projection: _id, idtype, idsource, name, details, operator, createdAt) */
    source?: { _id: string }
    /** Consentement valide (projection: _id, signed, signataire) */
    consentement?: unknown
    /** true si l'entreprise a accès à la source */
    hasAccess?: boolean
}

/** Premier point retourné par l’API Enedis recherche point (structure variable selon le SOAP) */
export type SearchPdlResult = {
    pdl?: string
    name?: string
    address?: string
    [key: string]: unknown
}

export type SearchPdlParamsParticulier = {
    street: string
    zipcode: string
    citycode: string
    lastname: string
}

export type SearchPdlParamsEntreprise = {
    street: string
    zipcode: string
    citycode: string
    name: string
    /** SIRET du titulaire (côté app : `company.idcompany`) — query `siret` vers l’API Enedis */
    siret?: string
}

/**
 * Jeux open data Enedis (API Data Fair) — lignes souterraines.
 * Requêtes listées via le proxy Vite `/api/opendata-enedis` → `opendata.enedis.fr`.
 */
export type EnedisUndergroundDataset =
    | "reseau-souterrain-bt"
    | "reseau-souterrain-hta"

/** Métadonnées jeu BT — base sans chemin `/lines` */
export const ENEDIS_DATAFAIR_DATASET_BT_URL =
    "/api/opendata-enedis/data-fair/api/v1/datasets/reseau-souterrain-bt"

/** Métadonnées jeu HTA — base sans chemin `/lines` */
export const ENEDIS_DATAFAIR_DATASET_HTA_URL =
    "/api/opendata-enedis/data-fair/api/v1/datasets/reseau-souterrain-hta"

/** Géométrie GeoJSON (WGS84) pour affichage carte */
export type EnedisUndergroundLineGeometry = {
    type: "LineString" | "MultiLineString"
    coordinates: unknown
}

/** Ligne normalisée pour la carte (synthèse API → Leaflet) */
export type EnedisUndergroundLineRecord = {
    _id: string
    geometry: EnedisUndergroundLineGeometry
    /**
     * Distance au point de recherche (m) si l’API la fournit ; sinon 0.
     */
    dist: number
}

/**
 * Champs utiles d’une ligne renvoyée par Data Fair (le reste est ignoré à l’affichage).
 */
export type EnedisDataFairUndergroundLineRow = {
    _id: string
    /** Souvent une chaîne JSON (GeoJSON) côté API */
    geometry?: string | EnedisUndergroundLineGeometry
    /** Géométrie GeoJSON calculée (préférée si présente) */
    _geoshape?: EnedisUndergroundLineGeometry
    /** Distance / score selon versions API (optionnel) */
    dist?: string | number
    _score?: number | null
}

/**
 * Réponse GET `.../datasets/{slug}/lines` (liste paginée).
 * @see https://opendata.enedis.fr/data-fair/api/v1/datasets/reseau-souterrain-bt/lines
 */
export type EnedisDataFairLinesListResponse = {
    total: number
    next?: string
    results: EnedisDataFairUndergroundLineRow[]
}

/**
 * URL des enregistrements géographiques (pas la fiche métadonnées du jeu).
 */
function getDataFairUndergroundLinesUrl(
    dataset: EnedisUndergroundDataset
): string {
    const base =
        dataset === "reseau-souterrain-bt"
            ? ENEDIS_DATAFAIR_DATASET_BT_URL
            : ENEDIS_DATAFAIR_DATASET_HTA_URL
    return `${base}/lines`
}

/**
 * Extrait une géométrie ligne exploitable par Leaflet depuis une ligne Data Fair.
 */
function parseUndergroundLineGeometry(
    row: EnedisDataFairUndergroundLineRow
): EnedisUndergroundLineGeometry | null {
    const fromShape = row._geoshape
    if (
        fromShape?.type === "LineString" ||
        fromShape?.type === "MultiLineString"
    ) {
        return fromShape
    }
    const raw = row.geometry
    if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw) as unknown
            if (
                parsed &&
                typeof parsed === "object" &&
                "type" in parsed &&
                ((parsed as EnedisUndergroundLineGeometry).type ===
                    "LineString" ||
                    (parsed as EnedisUndergroundLineGeometry).type ===
                        "MultiLineString")
            ) {
                return parsed as EnedisUndergroundLineGeometry
            }
        } catch {
            return null
        }
        return null
    }
    if (
        raw &&
        typeof raw === "object" &&
        (raw.type === "LineString" || raw.type === "MultiLineString")
    ) {
        return raw as EnedisUndergroundLineGeometry
    }
    return null
}

/**
 * Concatène les lignes d’`adresseInstallationNormalisee` (réponse SOAP Enedis recherche point).
 */
function formatAdresseInstallationNormalisee(raw: unknown): string | undefined {
    if (raw == null || typeof raw !== "object") return undefined
    const a = raw as Record<string, unknown>
    const order = [
        "ligne1",
        "ligne2",
        "ligne3",
        "ligne4",
        "ligne5",
        "ligne6",
        "ligne7",
    ] as const
    const parts: string[] = []
    for (const k of order) {
        const v = a[k]
        if (typeof v === "string" && v.trim()) parts.push(v.trim())
    }
    return parts.length ? parts.join(", ") : undefined
}

/** Fenêtre géographique WGS84 (Data Fair `bbox` : lon_min, lat_min, lon_max, lat_max) */
export type EnedisUndergroundSearchBbox = {
    lonMin: number
    latMin: number
    lonMax: number
    latMax: number
}

export type SearchUndergroundNetworkParams =
    | {
          dataset: EnedisUndergroundDataset
          rows?: number
          /** Point [lat, lng] + rayon (m) */
          localisation: [number, number]
          radius?: number
      }
    | {
          dataset: EnedisUndergroundDataset
          rows?: number
          /** Emprise carte observée */
          bbox: EnedisUndergroundSearchBbox
      }

export class EnedisService {
    /**
     * Lignes souterraines Enedis (open data).
     * Appelle `GET .../datasets/{slug}/lines` (Data Fair), pas la ressource métadonnées du jeu.
     *
     * Soit un rayon autour d’un point (`geo_distance`), soit une boîte (`bbox`).
     *
     * @param params.dataset - `reseau-souterrain-bt` ou `reseau-souterrain-hta`
     * @param params.rows - alias de `size` côté API (nombre max de lignes)
     * @see https://opendata.enedis.fr/datasets/reseau-souterrain-bt/api-doc?operation=readLines
     */
    async searchUndergroundNetwork(
        params: SearchUndergroundNetworkParams
    ): Promise<EnedisUndergroundLineRecord[]> {
        return handleServiceCall<EnedisUndergroundLineRecord[]>(async () => {
            const { dataset, rows = 200 } = params
            const url = getDataFairUndergroundLinesUrl(dataset)

            let queryParams: {
                geo_distance?: string
                bbox?: string
                size: number
            }
            if ("bbox" in params) {
                const { lonMin, latMin, lonMax, latMax } = params.bbox
                queryParams = {
                    bbox: `${lonMin},${latMin},${lonMax},${latMax}`,
                    size: rows,
                }
            } else {
                const { localisation, radius = 1000 } = params
                const [lat, lng] = localisation
                /** Format officiel : `lon:lat:distance_m` (deux-points). */
                queryParams = {
                    geo_distance: `${lng}:${lat}:${radius}`,
                    size: rows,
                }
            }

            const { data } = await axios.get<EnedisDataFairLinesListResponse>(
                url,
                {
                    params: queryParams,
                }
            )
            const list = data.results ?? []
            const out: EnedisUndergroundLineRecord[] = []
            for (const row of list) {
                const geometry = parseUndergroundLineGeometry(row)
                if (!geometry) continue
                out.push({
                    _id: row._id,
                    geometry,
                    dist: Number.parseFloat(String(row.dist ?? 0)),
                })
            }
            return out
        })
    }

    /**
     * Récupère les workflows Enedis d'une organisation
     * @param _id_company - Identifiant de l'organisation
     */
    async getWorkflowEnedisByCompanyId(
        _id_company: string
    ): Promise<GetWorkflowEnedisByCompanyIdResponse> {
        return handleServiceCall<GetWorkflowEnedisByCompanyIdResponse>(
            async () => {
                const { data } = await axios.get(
                    `/api/enedis/workflow/company/${_id_company}`
                )
                return {
                    data: data.data,
                    total: data.total,
                }
            }
        )
    }

    /**
     * Vérifie si le PDL existe dans Homeys (api-metier).
     * @param params.idsource - Identifiant réel du compteur (PDL)
     * @returns Données source/consentement/hasAccess si trouvé, false si not_found_source ou not_found_source_with_valid_consent
     */
    async verifyPdl(params: {
        idsource: string
    }): Promise<VerifyPdlResult | false> {
        try {
            const { data } = await axios.get(
                "/api/metier/v5/source/search/pdl",
                { params: { idsource: params.idsource } }
            )
            return data as VerifyPdlResult
        } catch (err: unknown) {
            const code =
                (
                    err as {
                        response?: { data?: { detail?: { code?: string } } }
                    }
                )?.response?.data?.detail?.code ?? "error_unknown"
            if (
                code === "not_found_source" ||
                code === "not_found_source_with_valid_consent"
            ) {
                return false
            }
            throw err
        }
    }

    /**
     * Recherche un point de livraison (PDL) via l’API Enedis – titulaire particulier.
     * @returns Premier point trouvé ou null en cas d’erreur / aucun résultat
     */
    async searchPdlParticulier(
        params: SearchPdlParamsParticulier
    ): Promise<SearchPdlResult | null> {
        try {
            const { data } = await axios.get(
                "/api/enedis/rechercher_point/particulier",
                {
                    params: {
                        street: params.street,
                        zipcode: params.zipcode,
                        citycode: params.citycode,
                        lastname: params.lastname,
                    },
                }
            )
            return this.normalizeSearchPdlResult(data)
        } catch {
            return null
        }
    }

    /**
     * Recherche un point de livraison (PDL) via l’API Enedis – titulaire entreprise.
     * @returns Premier point trouvé ou null en cas d’erreur / aucun résultat
     */
    async searchPdlEntreprise(
        params: SearchPdlParamsEntreprise
    ): Promise<SearchPdlResult | null> {
        try {
            const { data } = await axios.get(
                "/api/enedis/rechercher_point/entreprise",
                {
                    params: {
                        street: params.street,
                        zipcode: params.zipcode,
                        citycode: params.citycode,
                        name: params.name,
                        ...(params.siret != null && params.siret !== ""
                            ? { siret: params.siret }
                            : {}),
                    },
                }
            )
            return this.normalizeSearchPdlResult(data)
        } catch {
            return null
        }
    }

    /**
     * Normalise la réponse Enedis (objet brut) en SearchPdlResult (pdl, name, address).
     * Le PRM peut être dans `pdl`, `pointId` ou `id` selon les flux / versions SOAP.
     */
    private normalizeSearchPdlResult(raw: unknown): SearchPdlResult | null {
        if (raw == null || typeof raw !== "object") return null
        const o = raw as Record<string, unknown>
        const pdl =
            typeof o.pdl === "string"
                ? o.pdl
                : typeof o.pointId === "string"
                  ? o.pointId
                  : typeof o.id === "string"
                    ? o.id
                    : undefined
        const name =
            typeof o.name === "string"
                ? o.name
                : typeof o.nomClientFinalOuDenominationSociale === "string"
                  ? o.nomClientFinalOuDenominationSociale
                  : undefined
        const address =
            typeof o.address === "string"
                ? o.address
                : typeof o.numeroEtNomVoie === "string"
                  ? o.numeroEtNomVoie
                  : formatAdresseInstallationNormalisee(
                        o.adresseInstallationNormalisee
                    )
        if (!pdl) return null
        return { pdl, name, address, ...o }
    }
}
